import type {
  ZoomLevels,
  ZoomCenter,
} from "@/types/InteractiveMasterplanInterface";

export const getDeviceZoomLevels = (
  levels: ZoomLevels | undefined,
  mobile: boolean,
  landscape = false
) =>
  landscape
    ? levels?.mobileLandscape ?? levels?.mobile ?? levels
    : mobile
    ? levels?.mobile ?? levels
    : levels;

export const MIN_VIEW_ZOOM_GAP = 0.1;

export const getMinimumCoverZoom = (
  viewportWidth: number,
  viewportHeight: number,
  viewBoxWidth: number,
  viewBoxHeight: number,
  baseImageWidth = viewportWidth
): number => {
  if (
    viewportWidth <= 0 ||
    viewportHeight <= 0 ||
    viewBoxWidth <= 0 ||
    viewBoxHeight <= 0 ||
    baseImageWidth <= 0
  ) {
    return 1;
  }

  return Math.max(
    1,
    (viewportHeight * viewBoxWidth) / (baseImageWidth * viewBoxHeight)
  );
};

export const getViewThresholds = ({
  minimumZoom,
  estateZoom,
  stageZoom,
  lotZoom,
  hasEstateBoundary,
  stageDefaultOffset,
  lotDefaultOffset,
}: {
  minimumZoom: number;
  estateZoom?: number;
  stageZoom?: number;
  lotZoom?: number;
  hasEstateBoundary: boolean;
  stageDefaultOffset: number;
  lotDefaultOffset: number;
}) => {
  const estate = Math.max(minimumZoom, estateZoom ?? minimumZoom);
  const stages = hasEstateBoundary
    ? Math.max(
        estate + MIN_VIEW_ZOOM_GAP,
        stageZoom ?? estate + stageDefaultOffset
      )
    : minimumZoom;
  const lots = Math.max(
    stages + MIN_VIEW_ZOOM_GAP,
    lotZoom ?? (hasEstateBoundary ? estate : minimumZoom) + lotDefaultOffset
  );

  return { estate, stages, lots };
};

export const getCameraScroll = (
  center: { x: number; y: number },
  viewBox: { x: number; y: number; width: number; height: number },
  content: { width: number; height: number },
  viewport: { width: number; height: number }
) => ({
  left: Math.max(
    0,
    Math.min(
      Math.max(0, content.width - viewport.width),
      ((center.x - viewBox.x) / viewBox.width) * content.width -
        viewport.width / 2
    )
  ),
  top: Math.max(
    0,
    Math.min(
      Math.max(0, content.height - viewport.height),
      ((center.y - viewBox.y) / viewBox.height) * content.height -
        viewport.height / 2
    )
  ),
});

export const getDeviceZoomCenter = (
  settings:
    | {
        zoomLevels?: ZoomLevels;
        estateZoomCenter?: ZoomCenter;
        stageZoomCenter?: ZoomCenter;
      }
    | undefined,
  key: "estateZoomCenter" | "stageZoomCenter",
  mobile: boolean,
  landscape = false
): ZoomCenter | null | undefined => {
  const levels = getDeviceZoomLevels(settings?.zoomLevels, mobile, landscape);

  return (mobile || landscape) && levels?.[key] !== undefined
    ? levels[key]
    : settings?.[key];
};
